import { computed, defineComponent, reactive, ref } from 'vue';
import { useSweetAlert } from '@/composables/sweetalert';
import { useStore } from 'vuex';
import { CancelAction, CancelGetter } from '@/store/cancel/cancel.module';
import { CancelSchema } from '@/store/cancel/cancel.validator';
import { Cancel } from '@/store/cancel/cancel.model';
import Datepicker from '@vuepic/vue-datepicker';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'ProjectCancelModalComponent',
  components: {
    Datepicker,
    BaseModalComponent,
  },
  props: {
    projectId: {
      type: Number,
    }
  },
  emits: [
    'refresh'
  ],
  setup: (props, context) => {
    const swal = useSweetAlert();
    const store = useStore();
    const cancel = computed<Cancel | undefined>(() => store.getters[CancelGetter.GET]);
    const isEditMode = computed(() => !!cancel.value);
    const title = isEditMode.value ? 'แก้ไขยกเลิกโครงการ' : 'ยกเลิกโครงการ';
    const cancelForm = ref<InstanceType<typeof BaseModalComponent>>();
    const isRefresh = ref(false);

    // form
    interface CanceledForm {
      projectId?: number | null;
      canceledDate?: Date | null;
      remark?: string | null;
    }
    const formData = reactive<CanceledForm>({
      projectId: null,
      canceledDate: null,
      remark: ''
    });
    const validator = computed(() => CancelSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      canceledDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'canceledDate')),
      remark: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'remark')),
    });
    const isValid = computed(() => !errors.canceledDate && !errors.remark);
    const isLoading = computed<boolean>(() => store.getters[CancelGetter.STATUS] === 'init' || store.getters[CancelGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.projectId = props.projectId;

        if (isEditMode.value) {
          formData.canceledDate = cancel.value?.canceledDate;
          formData.remark = cancel.value?.remark;
        }

        resolve();
      }).then(() => cancelForm.value?.show());
    };

    // hide modal
    const hide = () => {
      cancelForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.projectId = null;
        formData.canceledDate = null;
        formData.remark = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        store.dispatch(!isEditMode.value ? CancelAction.CREATE : CancelAction.UPDATE, formData)
          .then(() => {
            isRefresh.value = true;
            hide();
          });
      }
    };

    // delete
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(CancelAction.DELETE, { projectId: props.projectId })
              .then(() => {
                isRefresh.value = true;
                hide();
              });
          }
        });
    };

    const refresh = () => {
      if (isRefresh.value) {
        isRefresh.value = false;
        context.emit('refresh');
      }
    };

    return {
      title,
      isEditMode,
      cancelForm,
      formData,
      errors,
      submited,
      isLoading,
      show,
      hide,
      submitClick,
      deleteClick,
      unsubmit,
      clearForm,
      refresh,
    };
  },
});
